import React, { useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Heart, Activity, Weight, Wind } from "lucide-react";
import api from "../lib/api";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

const ranges = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "6m", label: "6 months" },
];

const getRangeStart = (rangeKey) => {
  const now = new Date();
  const start = new Date(now);

  if (rangeKey === "7d") {
    start.setDate(now.getDate() - 7);
    return start;
  }

  if (rangeKey === "30d") {
    start.setDate(now.getDate() - 30);
    return start;
  }

  start.setMonth(now.getMonth() - 6);
  return start;
};

const formatLabel = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const hasSeriesValues = (values = []) => values.some((value) => typeof value === "number");

const getVitalsData = (rangeKey, readings = []) => {
  const start = getRangeStart(rangeKey);
  const filteredReadings = readings.filter((reading) => {
    if (!reading?.createdAt) return false;
    return new Date(reading.createdAt) >= start;
  });

  const labels = filteredReadings.map((reading) => formatLabel(reading.createdAt));

  const systolic = filteredReadings.map((reading) => reading.bloodPressure?.systolic ?? null);

  const diastolic = filteredReadings.map((reading) => reading.bloodPressure?.diastolic ?? null);

  const heart = filteredReadings.map((reading) => reading.heartRate ?? null);

  const weightSeries = filteredReadings.map((reading) => reading.weight ?? null);

  const oxygenSeries = filteredReadings.map((reading) => reading.oxygenLevel ?? null);

  return {
    hasData: filteredReadings.length > 0,
    bloodPressure: {
      labels,
      datasets: [
        {
          label: "Systolic",
          data: systolic,
          borderColor: "#7c4dff",
          backgroundColor: "rgba(124,77,255,0.1)",
          tension: 0.3,
          pointRadius: 2,
          spanGaps: true,
        },
        {
          label: "Diastolic",
          data: diastolic,
          borderColor: "#26a69a",
          backgroundColor: "rgba(38,166,154,0.1)",
          tension: 0.3,
          pointRadius: 2,
          spanGaps: true,
        },
      ],
    },
    weight: {
      labels,
      datasets: [
        {
          label: "Weight (kg)",
          data: weightSeries,
          borderColor: "#5e35b1",
          backgroundColor: "rgba(94,53,177,0.1)",
          tension: 0.3,
          pointRadius: 2,
          spanGaps: true,
        },
      ],
    },
    oxygen: {
      labels,
      datasets: [
        {
          label: "Oxygen level (%)",
          data: oxygenSeries,
          borderColor: "#0ea5e9",
          backgroundColor: "rgba(14,165,233,0.1)",
          tension: 0.3,
          pointRadius: 2,
          spanGaps: true,
        },
      ],
    },
    heartRate: {
      labels,
      datasets: [
        {
          label: "Heart rate (bpm)",
          data: heart,
          borderColor: "#e53935",
          backgroundColor: "rgba(229,57,53,0.1)",
          tension: 0.3,
          pointRadius: 2,
          spanGaps: true,
        },
      ],
    },
    seriesState: {
      bloodPressure: hasSeriesValues(systolic) || hasSeriesValues(diastolic),
      weight: hasSeriesValues(weightSeries),
      oxygen: hasSeriesValues(oxygenSeries),
      heartRate: hasSeriesValues(heart),
    },
  };
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      labels: { usePointStyle: true, boxWidth: 8 },
    },
    tooltip: {
      intersect: false,
      mode: "index",
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { maxRotation: 0, autoSkip: true },
    },
    y: {
      grid: { color: "rgba(0,0,0,0.05)" },
      ticks: { precision: 0 },
    },
  },
};

const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
    <Icon size={20} style={{ marginRight: "8px", color: "#704af6" }} />
    <div>
      <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#333" }}>{title}</div>
      {subtitle && <div style={{ fontSize: "0.8rem", color: "#888" }}>{subtitle}</div>}
    </div>
  </div>
);

const VitalsHistory = () => {
  const [range, setRange] = useState("7d");
  const [readings, setReadings] = useState([]);
  const data = useMemo(() => getVitalsData(range, readings), [range, readings]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await api.get('/api/vitals?limit=240');
        if (mounted && res.data && res.data.vitals) {
          setReadings(res.data.vitals.reverse()); // oldest -> newest
        }
      } catch (err) {
        console.warn('Failed to load vitals for history', err?.message || err);
      }
    };
    load();
    return () => (mounted = false);
  }, []);

  return (
    <div style={{ padding: "16px" }}>
      {/* Header with Range Selector */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#fff",
          padding: "12px 16px",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          marginBottom: "16px",
        }}
      >
        <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#333" }}>
          Vitals history
        </div>

        <div style={{ display: "flex", gap: "8px" }}>
          {ranges.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              style={{
                padding: "6px 12px",
                borderRadius: "8px",
                border: "1px solid",
                borderColor: range === r.key ? "#704af6" : "#ccc",
                background: range === r.key ? "#704af6" : "#fff",
                color: range === r.key ? "#fff" : "#333",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 500,
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid for Vitals Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "16px",
        }}
      >
        {/* Blood Pressure */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "16px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            height: "260px",
          }}
        >
          <SectionHeader icon={Activity} title="Blood pressure trend" subtitle="Systolic & diastolic" />
          <div style={{ height: "180px" }}>
              {data.seriesState.bloodPressure ? (
                <Line data={data.bloodPressure} options={chartOptions} />
              ) : (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontSize: "0.85rem" }}>
                  No blood pressure readings in this period
                </div>
              )}
          </div>
        </div>

        {/* Weight */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "16px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            height: "260px",
          }}
        >
          <SectionHeader icon={Weight} title="Weight over time" />
          <div style={{ height: "180px" }}>
            {data.seriesState.weight ? (
              <Line data={data.weight} options={chartOptions} />
            ) : (
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontSize: "0.85rem" }}>
                No weight readings in this period
              </div>
            )}
          </div>
        </div>

        {/* Oxygen */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "16px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            height: "260px",
          }}
        >
          <SectionHeader icon={Wind} title="Oxygen level" />
          <div style={{ height: "180px" }}>
            {data.seriesState.oxygen ? (
              <Line data={data.oxygen} options={chartOptions} />
            ) : (
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontSize: "0.85rem" }}>
                No oxygen readings in this period
              </div>
            )}
          </div>
        </div>

        {/* Heart Rate */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "16px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            height: "260px",
          }}
        >
          <SectionHeader icon={Heart} title="Heart rate" />
          <div style={{ height: "180px" }}>
            {data.seriesState.heartRate ? (
              <Line data={data.heartRate} options={chartOptions} />
            ) : (
              <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontSize: "0.85rem" }}>
                No heart rate readings in this period
              </div>
            )}
          </div>
        </div>
      </div>

      {!data.hasData && (
        <div style={{ marginTop: 12, textAlign: "center", color: "#777", fontSize: "0.85rem" }}>
          No vitals recorded yet. Add vitals to see trends.
        </div>
      )}
    </div>
  );
};

export default VitalsHistory;
